/**
 * useRectangleDrag — bulk-select cards by drawing a rectangle on the container.
 *
 * Per FWV v8 §30: vanilla DOM listeners extracted from TodayView into its own
 * hook so the rendering surface stays focused on layout. Uses CALLBACK REF
 * (not useEffect+ref) so listeners bind correctly even when the container
 * renders conditionally. The callback ref also handles React StrictMode's
 * intentional double-invoke by cleaning up the previous listener before
 * re-binding.
 *
 * Filter: mousedown on a card body, action bar, or form input does NOT
 * start a rectangle drag. Otherwise the rectangle starts at the cursor.
 */
export type Rect = { x: number; y: number; w: number; h: number } | null

export type RectangleDragOptions = {
  setRect: (r: Rect) => void
  onSelect: (ids: string[]) => void
  clearSelectionOnStart: () => void
}

/**
 * Build the callback ref that binds the rectangle-drag listeners.
 * Returns the ref function; pass it to the container's `ref` prop.
 *
 * The callback cleans up its previous binding on every call (handles
 * StrictMode double-invoke + late conditional-render).
 */
export function buildRectangleDragRef(opts: RectangleDragOptions) {
  return function setContainer(node: HTMLDivElement | null) {
    // Cleanup previous binding (StrictMode + re-render safety)
    const w = window as any
    if (w.__rbsCurrentCleanup) {
      w.__rbsCurrentCleanup()
      w.__rbsCurrentCleanup = null
    }
    if (!node) return

    let startX = 0
    let startY = 0
    let active = false

    function onMouseDown(e: MouseEvent) {
      const target = e.target as HTMLElement
      if (target.closest('[data-card-root]')) return
      if (target.closest('[data-rubber-band-action]')) return
      if (target.closest('button, input, select, textarea')) return

      startX = e.clientX
      startY = e.clientY
      active = true
      opts.setRect({ x: startX, y: startY, w: 0, h: 0 })
      if (!e.shiftKey) opts.clearSelectionOnStart()
    }

    function onMouseMove(e: MouseEvent) {
      if (!active) return
      const x = Math.min(startX, e.clientX)
      const y = Math.min(startY, e.clientY)
      const w = Math.abs(e.clientX - startX)
      const h = Math.abs(e.clientY - startY)
      opts.setRect({ x, y, w, h })
    }

    function onMouseUp(e: MouseEvent) {
      if (!active) return
      active = false
      const x = Math.min(startX, e.clientX)
      const y = Math.min(startY, e.clientY)
      const w = Math.abs(e.clientX - startX)
      const h = Math.abs(e.clientY - startY)
      opts.setRect(null)

      if (w < 5 && h < 5) return
      if (!node) return

      const next = new Set<string>()
      node.querySelectorAll('[data-card-root][data-card-id]').forEach((el) => {
        const r = (el as HTMLElement).getBoundingClientRect()
        if (!(x + w < r.x || r.x + r.width < x || y + h < r.y || r.y + r.height < y)) {
          const id = (el as HTMLElement).dataset.cardId
          if (id) next.add(id)
        }
      })
      opts.onSelect(Array.from(next))
    }

    node.addEventListener('mousedown', onMouseDown)
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)

    w.__rbsCurrentCleanup = () => {
      node.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
  }
}